import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ImageOff } from "lucide-react";
import { useAuthedApi, errMsg, imgUrl } from "../../lib/api";
import { Switch } from "../ui/switch";
import StateCitySelect from "../StateCitySelect";
import BannerSlider from "../BannerSlider";
import BannerForm from "./BannerForm";

const today = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
const liveNow = (b) => b.active && (!b.start_at || b.start_at <= today()) && (!b.end_at || b.end_at >= today());

export default function AdminBanners() {
  const api = useAuthedApi();
  const [loc, setLoc] = useState({ state: "Maharashtra", city: "Mumbai" });
  const statewide = loc.city === "__all__";
  const label = (c) => (c === "__all__" ? "All cities" : c);
  const [list, setList] = useState(null);
  const [summary, setSummary] = useState([]);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(0);

  const load = () => {
    if (loc.city) api("get", "/admin/banners", null, { params: loc }).then(setList); else setList([]);
    api("get", "/admin/banners/summary").then(setSummary);
    setPreview((p) => p + 1);
  };
  useEffect(load, [loc.state, loc.city]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const save = (b, patch) => act(() => api("put", `/admin/banners/${b.id}`, { state: b.state, city: b.city, image_url: b.image_url, title: b.title, link: b.link, order: b.order, active: b.active, start_at: b.start_at, end_at: b.end_at, ...patch }), "Banner updated");
  const move = (k, d) => {
    const ids = list.map((b) => b.id);
    [ids[k], ids[k + d]] = [ids[k + d], ids[k]];
    act(() => api("post", "/admin/banners/reorder", { ...loc, ids }), "Order saved");
  };

  return (
    <div className="space-y-5" data-testid="admin-banners">
      <div className="lux-card p-4 flex flex-wrap items-end gap-3">
        <div className="grid grid-cols-2 gap-3 flex-1 min-w-[280px] max-w-xl"><StateCitySelect state={loc.state} city={statewide ? "" : loc.city} prefix="banner-loc-" onChange={(v) => setLoc({ ...loc, ...v, city: statewide ? "__all__" : v.city })} /></div>
        <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer pb-2"><Switch className="data-[state=unchecked]:bg-gray-600" checked={statewide} onCheckedChange={(v) => setLoc({ ...loc, city: v ? "__all__" : "" })} data-testid="banner-statewide-toggle" />State-wide (all cities in {loc.state || "state"})</label>
        <button className="btn-gold text-sm" disabled={!loc.city} onClick={() => setEditing({})} data-testid="banner-add-btn"><Plus className="w-4 h-4" />Add banner</button>
      </div>

      {summary.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="banner-summary">
          {summary.map((s) => (
            <button key={s.state + s.city} onClick={() => setLoc({ state: s.state, city: s.city })} className={`px-3 py-1 rounded-full text-xs border ${s.city === loc.city && s.state === loc.state ? "bg-[#D4AF37] text-black border-transparent" : "border-white/10 text-gray-300 hover:border-[#D4AF37]"}`}>{s.city === "__all__" ? `All ${s.state}` : s.city} · {s.active}/{s.count}</button>
          ))}
        </div>
      )}

      {loc.city && (
        <div>
          <p className="eyebrow !text-[10px] mb-2">Live preview — {label(loc.city)}{statewide ? ` in ${loc.state}` : " (includes state-wide banners)"}</p>
          <BannerSlider key={preview} preview state={loc.state} city={loc.city} />
        </div>
      )}

      <div className="space-y-2">
        {list?.map((b, k) => (
          <div key={b.id} className="lux-card p-3 flex flex-wrap items-center gap-3" data-testid={`admin-banner-row-${b.id}`}>
            <div className="w-36 aspect-[16/5] rounded-lg overflow-hidden bg-black border border-white/5 shrink-0">
              {b.image_url ? <img src={imgUrl(b.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-5 h-5 m-auto text-gray-600" />}
            </div>
            <div className="flex-1 min-w-[160px]">
              <p className="text-sm font-semibold">{b.title || <span className="text-gray-500 italic">No title</span>}</p>
              <p className="text-[11px] text-gray-400 truncate">{b.link || "No link"}</p>
              <p className="text-[11px] text-gray-500">{b.start_at || "Any start"} → {b.end_at || "No end"} · <span className={liveNow(b) ? "text-emerald-400" : "text-amber-400"} data-testid={`admin-banner-status-${b.id}`}>{liveNow(b) ? "Live now" : b.active ? "Scheduled/expired" : "Off"}</span></p>
              <p className="text-[11px] text-gray-300 mt-0.5" data-testid={`admin-banner-stats-${b.id}`}><span className="gold-text font-semibold">{b.views || 0}</span> views · <span className="gold-text font-semibold">{b.clicks || 0}</span> taps · {b.views ? ((100 * (b.clicks || 0)) / b.views).toFixed(1) : "0.0"}% tap rate</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button disabled={k === 0} onClick={() => move(k, -1)} className="w-8 h-8 rounded-full border border-white/10 disabled:opacity-30 flex items-center justify-center" aria-label="Move up" data-testid={`banner-up-${b.id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
              <button disabled={k === list.length - 1} onClick={() => move(k, 1)} className="w-8 h-8 rounded-full border border-white/10 disabled:opacity-30 flex items-center justify-center" aria-label="Move down" data-testid={`banner-down-${b.id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
              <Switch className="data-[state=unchecked]:bg-gray-600 mx-2" checked={b.active} onCheckedChange={(v) => save(b, { active: v })} data-testid={`banner-active-${b.id}`} />
              <button className="btn-outline !px-3 !py-1.5 text-xs" onClick={() => setEditing(b)} data-testid={`banner-edit-${b.id}`}><Pencil className="w-3 h-3" />Edit</button>
              <button className="btn-outline !px-3 !py-1.5 text-xs !text-red-400 !border-red-500/40" onClick={() => window.confirm("Delete this banner?") && act(() => api("delete", `/admin/banners/${b.id}`), "Banner deleted")} data-testid={`banner-delete-${b.id}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
        {list && !list.length && loc.city && <div className="lux-card p-10 text-center text-gray-400" data-testid="banner-empty">No banners for {label(loc.city)} yet — visitors see the default gold banner.</div>}
      </div>

      {editing && <BannerForm banner={editing} loc={loc} nextOrder={list?.length || 0} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
